import { motion } from "framer-motion";
import { ArrowLeftRight, ArrowUpRight, ArrowDownLeft } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// FIN-2 — ExtratoHeader
// Header com 2 KPIs heroicos (Entradas + Saídas) e badge sutil de saldo.
// Gradient border cyan + glow hover. Tipografia heroica 64-72px.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function Kpi({ label, value, sign, tone, icon: Icon, testId }) {
  const TONE_TEXT = {
    emerald: "text-brand-green",
    error: "text-status-error",
  };
  const TONE_BG = {
    emerald: "bg-brand-green/10",
    error: "bg-status-error/10",
  };
  return (
    <div className="flex-1 min-w-0" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className={cn("w-7 h-7 rounded-md flex items-center justify-center shrink-0", TONE_BG[tone])}>
          <Icon className={cn("w-4 h-4", TONE_TEXT[tone])} aria-hidden="true" />
        </div>
        <span
          className="text-caption text-foreground-muted uppercase font-semibold"
          style={{ letterSpacing: "0.10em" }}
        >
          {label}
        </span>
      </div>
      <div
        className={cn(
          "font-display font-bold leading-[0.95] tracking-[-0.02em] tabular-nums truncate",
          "text-[44px] md:text-[56px] lg:text-[64px]",
          TONE_TEXT[tone],
        )}
      >
        {sign}{fmtBRL(value)}
      </div>
    </div>
  );
}

export default function ExtratoHeader({ summary, totalCount, isLoading }) {
  const entradas = Number(summary?.entradas || 0);
  const saidas = Number(summary?.saidas || 0);
  const saldo = Number(summary?.saldo || 0);
  const isPositive = saldo >= 0;

  return (
    <Card
      variant="elevated"
      className="glass-card-strong gradient-border-cyan glow-hover p-6 md:p-8"
      data-testid="extrato-header"
    >
      {/* Top row — title + count */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-cyan/15 border border-brand-cyan/30 flex items-center justify-center">
            <ArrowLeftRight className="w-5 h-5 text-brand-cyan" aria-hidden="true" />
          </div>
          <div>
            <h2 className="font-display font-bold text-foreground text-h2 leading-tight">
              Extrato
            </h2>
            <p
              className="text-caption text-foreground-muted"
              data-testid="extrato-tx-count"
            >
              {isLoading ? "…" : `${totalCount.toLocaleString("pt-BR")} transações`}
            </p>
          </div>
        </div>
      </div>

      {/* KPIs heroicos */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
        className="flex flex-col md:flex-row gap-6 md:gap-12"
      >
        <Kpi
          label="Entradas"
          value={entradas}
          sign="+"
          tone="emerald"
          icon={ArrowDownLeft}
          testId="extrato-kpi-entradas"
        />
        <Kpi
          label="Saídas"
          value={saidas}
          sign="−"
          tone="error"
          icon={ArrowUpRight}
          testId="extrato-kpi-saidas"
        />
      </motion.div>

      {/* Saldo badge sutil */}
      <div className="mt-5 flex items-center">
        <Badge
          variant={isPositive ? "success" : "danger"}
          className="text-caption"
          data-testid="extrato-badge-saldo"
        >
          Saldo no período: {isPositive ? "+" : "−"}{fmtBRL(Math.abs(saldo))}
        </Badge>
      </div>
    </Card>
  );
}
